const STORAGE_KEY = 'instagram_onboarding_state';
const FALLBACK_STORAGE_KEY = 'instagram_onboarding_state_fallback';

type InstagramOnboardingState = {
  orgId: string;
  nonce: string;
  createdAt: number;
};

const encodeState = (value: InstagramOnboardingState) =>
  window.btoa(JSON.stringify(value));

const decodeState = (value: string): InstagramOnboardingState | null => {
  try {
    return JSON.parse(window.atob(value)) as InstagramOnboardingState;
  } catch {
    return null;
  }
};

const createNonce = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createInstagramOnboardingState = (orgId: string) => {
  const payload: InstagramOnboardingState = {
    orgId,
    nonce: createNonce(),
    createdAt: Date.now(),
  };

  const serialized = JSON.stringify(payload);
  sessionStorage.setItem(STORAGE_KEY, serialized);
  localStorage.setItem(FALLBACK_STORAGE_KEY, serialized);
  return encodeState(payload);
};

export const consumeInstagramOnboardingState = (
  encodedState?: string | null,
  expectedOrgId?: string | null,
) => {
  const rawStored =
    sessionStorage.getItem(STORAGE_KEY) ||
    localStorage.getItem(FALLBACK_STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(FALLBACK_STORAGE_KEY);

  if (!encodedState || !rawStored) {
    const decodedOnly = encodedState ? decodeState(encodedState) : null;
    const orgMatches =
      decodedOnly &&
      (!expectedOrgId || decodedOnly.orgId === expectedOrgId);

    return {
      valid: Boolean(orgMatches),
      orgId: decodedOnly?.orgId || null,
      weakValidation: Boolean(orgMatches),
    };
  }

  try {
    const stored = JSON.parse(rawStored) as InstagramOnboardingState;
    const received = decodeState(encodedState);

    if (!received) {
      return { valid: false, orgId: null as string | null, weakValidation: false };
    }

    const isFresh = Date.now() - stored.createdAt <= 15 * 60 * 1000;
    const sameNonce = stored.nonce === received.nonce;
    const sameOrg = stored.orgId === received.orgId;
    const matchesExpectedOrg = !expectedOrgId || received.orgId === expectedOrgId;

    if (!isFresh || !sameNonce || !sameOrg || !matchesExpectedOrg) {
      return {
        valid: false,
        orgId: received.orgId || null,
        weakValidation: false,
      };
    }

    return { valid: true, orgId: received.orgId, weakValidation: false };
  } catch {
    return { valid: false, orgId: null as string | null, weakValidation: false };
  }
};
